import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import toast from "react-hot-toast";
import { Upload, Image as ImageIcon, Plus, X, Edit3 } from "lucide-react";
import { chatService } from "../../services/chatService";
import DeleteConfirmationModal from "../modals/DeleteConfirmationModal";

const ManualContentTab = React.forwardRef(
  (
    {
      agentData,
      knowledgeItems = [],
      onKnowledgeAdd,
      editKnowledge = null,
      onKnowledgeUpdate,
      onCancelEdit,
      onDeleteKnowledge,
    },
    ref
  ) => {
    // Tab management state
    const [tabs, setTabs] = useState([]);
    const [activeTabId, setActiveTabId] = useState(null);
    const [editingTabId, setEditingTabId] = useState(null);
    const [editingTabName, setEditingTabName] = useState("");

    // Modal state for delete confirmation
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [tabToDelete, setTabToDelete] = useState(null);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    // Refs for file handling and editor
    const fileInputRef = useRef(null);
    const quillRef = useRef(null);
    const editTabInputRef = useRef(null);

    // Tab click handler with immediate content sync
    const handleTabClick = useCallback(
      (tabId) => {
        setActiveTabId(tabId);

        // Immediate content sync to prevent delay
        setTimeout(() => {
          if (quillRef.current) {
            const targetTab = tabs.find((tab) => tab.id === tabId);
            if (targetTab) {
              const editor = quillRef.current.getEditor();
              const newContent = targetTab.content || "";
              editor.clipboard.dangerouslyPasteHTML(newContent);
            }
          }
        }, 0);
      },
      [tabs]
    );

    // Tab helper functions
    const createNewTab = React.useCallback(() => {
      const newTab = {
        id: `tab-${Date.now()}`,
        name: `Untitled ${Date.now().toString().slice(-3)}`, // Use timestamp for unique naming
        content: "",
        isDirty: false,
        isNew: true,
      };

      setTabs((prev) => [...prev, newTab]);
      setActiveTabId(newTab.id);

      return newTab;
    }, []); // Stable reference

    const closeTab = (tabId, e) => {
      e.stopPropagation();

      const tab = tabs.find((t) => t.id === tabId);

      // Handle cancel edit for edit tabs
      if (tab && tab.isEdit && onCancelEdit) {
        onCancelEdit();
        return;
      }

      // Handle delete existing knowledge - use modal instead of window.confirm
      if (tab && tab.originalId && !tab.isNew && onDeleteKnowledge) {
        setTabToDelete(tab);
        setShowDeleteModal(true);
        return;
      }

      // Handle close new unsaved tabs
      if (tab && tab.isDirty) {
        const confirmClose = window.confirm(
          `You have unsaved changes in "${tab.name}". Are you sure you want to close this tab?`
        );
        if (!confirmClose) return;
      }

      setTabs((prev) => prev.filter((t) => t.id !== tabId));

      if (activeTabId === tabId) {
        const remainingTabs = tabs.filter((t) => t.id !== tabId);
        setActiveTabId(
          remainingTabs.length > 0
            ? remainingTabs[remainingTabs.length - 1].id
            : null
        );
      }
    };

    // Modal handlers
    const handleDeleteConfirm = async () => {
      if (tabToDelete && onDeleteKnowledge) {
        try {
          await onDeleteKnowledge(tabToDelete.originalId);
          // Close modal and reset state
          setShowDeleteModal(false);
          setTabToDelete(null);
        } catch (error) {
          // Error handling is done by parent component
          console.error("Error deleting knowledge:", error);
        }
      }
    };

    const handleDeleteCancel = () => {
      setShowDeleteModal(false);
      setTabToDelete(null);
    };

    const updateTabContent = (content) => {
      if (!activeTabId) return;

      setTabs((prev) =>
        prev.map((tab) =>
          tab.id === activeTabId ? { ...tab, content, isDirty: true } : tab
        )
      );
    };

    const getActiveTab = () => {
      return tabs.find((tab) => tab.id === activeTabId);
    };

    const startEditingTabName = (tabId, currentName) => {
      setEditingTabId(tabId);
      setEditingTabName(currentName);
      setTimeout(() => {
        editTabInputRef.current?.focus();
        editTabInputRef.current?.select();
      }, 0);
    };

    const saveTabName = () => {
      if (!editingTabName.trim()) {
        toast.error("Tab name cannot be empty");
        return;
      }

      setTabs((prev) =>
        prev.map((tab) =>
          tab.id === editingTabId
            ? { ...tab, name: editingTabName.trim(), isDirty: true }
            : tab
        )
      );

      setEditingTabId(null);
      setEditingTabName("");
    };

    const cancelEditTabName = () => {
      setEditingTabId(null);
      setEditingTabName("");
    };

    const openEditTab = (knowledge) => {
      // Check if edit tab for this knowledge already exists
      const existingTab = tabs.find(
        (tab) => tab.isEdit && tab.originalId === knowledge.id
      );

      if (existingTab) {
        // Switch to existing tab
        setActiveTabId(existingTab.id);
        return;
      }

      // Create new edit tab
      const editTab = {
        id: `edit-${knowledge.id}-${Date.now()}`,
        name: knowledge.title,
        content: knowledge.content,
        isDirty: false,
        isNew: false,
        isEdit: true,
        originalId: knowledge.id,
      };

      setTabs((prev) => [...prev, editTab]);
      setActiveTabId(editTab.id);
    };

    // Expose openEditTab function via ref
    React.useImperativeHandle(ref, () => ({
      openEditTab,
    }));

    // Initialize tabs from knowledge items or edit knowledge
    useEffect(() => {
      // Handle edit knowledge mode (single item edit)
      if (editKnowledge) {
        const editTab = {
          id: `edit-${editKnowledge.id}`,
          name: editKnowledge.title,
          content: editKnowledge.content,
          isDirty: false,
          isNew: false,
          isEdit: true,
          originalId: editKnowledge.id,
          knowledgeItem: editKnowledge,
        };

        setTabs([editTab]);
        setActiveTabId(editTab.id);
        return;
      }

      // Initialize tabs from knowledge items (normal mode)
      // Only initialize if we don't have tabs yet or knowledge items changed significantly
      const currentKnowledgeIds = tabs
        .filter((tab) => !tab.isNew && !tab.isEdit)
        .map((tab) => tab.originalId)
        .sort();

      const newKnowledgeIds = knowledgeItems
        .filter((item) => item.type === "manual" || item.type === "text")
        .map((item) => item.id)
        .sort();

      const hasChanged =
        JSON.stringify(currentKnowledgeIds) !== JSON.stringify(newKnowledgeIds);

      if (hasChanged) {
        if (knowledgeItems.length > 0) {
          const knowledgeTabs = knowledgeItems
            .filter((item) => item.type === "manual" || item.type === "text")
            .map((item) => ({
              id: `knowledge-${item.id}`,
              name: item.title,
              content: item.content,
              isDirty: false,
              isNew: false,
              isEdit: false,
              originalId: item.id,
              knowledgeItem: item,
            }));

          setTabs(knowledgeTabs);

          // Set first tab as active if no active tab or current active tab is not in new tabs
          const activeTabExists = knowledgeTabs.some(
            (tab) => tab.id === activeTabId
          );
          if (knowledgeTabs.length > 0 && (!activeTabId || !activeTabExists)) {
            setActiveTabId(knowledgeTabs[0].id);
          }
        } else {
          // No knowledge items, start with empty tabs
          setTabs([]);
          setActiveTabId(null);
        }
      }

      // Clear tabs when exiting edit mode
      if (!editKnowledge && tabs.length > 0 && tabs.some((tab) => tab.isEdit)) {
        setTabs([]);
        setActiveTabId(null);
      }
    }, [
      editKnowledge,
      knowledgeItems.length,
      JSON.stringify(
        knowledgeItems.map((item) => ({ id: item.id, title: item.title }))
      ),
    ]); // More stable dependencies

    // Force ReactQuill content sync when active tab changes
    useEffect(() => {
      if (quillRef.current && activeTabId) {
        const activeTab = getActiveTab();
        if (activeTab) {
          // Force update ReactQuill content
          const editor = quillRef.current.getEditor();
          const currentContent = editor.root.innerHTML;
          const newContent = activeTab.content || "";

          // Only update if content is different to prevent cursor jumping
          if (currentContent !== newContent) {
            editor.clipboard.dangerouslyPasteHTML(newContent);
          }
        }
      }
    }, [activeTabId, tabs]); // Include tabs to ensure we have latest content
    // File validation helper
    const validateImageFile = (file) => {
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];

      if (!file) {
        return { valid: false, error: "No file selected" };
      }

      if (!allowedTypes.includes(file.type)) {
        return {
          valid: false,
          error: "Please select image files only (jpg, png, gif, webp)",
        };
      }

      const maxSize = 50 * 1024 * 1024; // 50MB
      if (file.size > maxSize) {
        return {
          valid: false,
          error: "Image file is too large. Maximum size is 50MB",
        };
      }

      return { valid: true, error: null };
    };

    // Insert image URL to ReactQuill editor as plain text - EXACT SAME LOGIC
    const insertImageToEditor = (imageUrl) => {
      if (!quillRef.current) return;

      const quill = quillRef.current.getEditor();

      // Get current cursor position
      const currentSelection = quill.getSelection();

      if (currentSelection && currentSelection.index !== null) {
        // Ada cursor position - insert URL di posisi cursor
        quill.insertText(currentSelection.index, imageUrl);
        // Move cursor after URL
        quill.setSelection(currentSelection.index + imageUrl.length);
      } else {
        // Tidak ada cursor/belum aktif - insert di paling atas
        quill.insertText(0, imageUrl);
        // Move cursor after URL
        quill.setSelection(imageUrl.length);
      }

      // Focus back to editor setelah insert
      quill.focus();

      // Update active tab content with new URL - NEW: Update tab state
      const activeTab = getActiveTab();
      if (activeTab) {
        const newContent = quill.root.innerHTML;
        updateTabContent(newContent);
      }
    };

    // Main image upload handler - EXACT SAME LOGIC
    const handleImageUpload = async (file) => {
      if (!file) return;

      // Validate file
      const validation = validateImageFile(file);
      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }

      try {
        setIsUploading(true);

        // Upload image using existing chat service - SAME
        const response = await chatService.uploadImage(file);

        // Insert image to editor - SAME
        insertImageToEditor(response.url);

        // Success toast - SAME
        toast.success("Gambar berhasil diupload dan URL ditambahkan ke editor");
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.error(
          "Gagal upload gambar: " + (error.message || "Terjadi kesalahan")
        );
      } finally {
        setIsUploading(false);
      }
    };

    // Upload button handler - EXACT SAME
    const handleUploadButtonClick = () => {
      fileInputRef.current?.click();
    };

    // File input change handler - EXACT SAME
    const handleFileInputChange = (e) => {
      const file = e.target.files?.[0];
      if (file) {
        handleImageUpload(file);
      }
      // Reset input value to allow same file selection
      e.target.value = "";
    };

    // Clipboard paste handler - EXACT SAME PREVENTION LOGIC
    useEffect(() => {
      const handlePaste = (e) => {
        const items = Array.from(e.clipboardData?.items || []);
        const imageItem = items.find((item) => item.type.startsWith("image/"));

        if (imageItem) {
          // CRITICAL: Prevent ReactQuill default image paste behavior - SAME
          e.preventDefault();
          e.stopImmediatePropagation();

          const file = imageItem.getAsFile();
          if (file) {
            handleImageUpload(file);
          }
          return false;
        }
      };

      // Attach to ReactQuill editor with capture phase to intercept BEFORE ReactQuill - SAME
      const quillEditor = quillRef.current?.getEditor()?.container;
      if (quillEditor) {
        quillEditor.addEventListener("paste", handlePaste, {
          capture: true, // Capture phase - before ReactQuill processes
          passive: false, // Allow preventDefault
        });

        return () => {
          quillEditor.removeEventListener("paste", handlePaste, {
            capture: true,
          });
        };
      }
    }, [activeTabId]); // NEW: Re-setup when active tab changes

    // Drag and drop handlers - EXACT SAME LOGIC
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Only hide if we're leaving the entire drop zone
      if (!e.currentTarget.contains(e.relatedTarget)) {
        setIsDragging(false);
      }
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer?.files || []);
      const imageFile = files.find((file) => file.type.startsWith("image/"));

      if (imageFile) {
        handleImageUpload(imageFile);
      } else if (files.length > 0) {
        toast.error("Please drop image files only");
      }
    };

    const handleSubmit = async (e) => {
      e.preventDefault();

      const activeTab = getActiveTab();
      if (!activeTab) {
        toast.error("No active tab");
        return;
      }

      if (!activeTab.name.trim()) {
        toast.error("Tab name (title) is required");
        return;
      }

      if (!activeTab.content.trim() || activeTab.content === "<p><br></p>") {
        toast.error("Content is required");
        return;
      }

      setIsSubmitting(true);
      try {
        if ((activeTab.isEdit || activeTab.originalId) && onKnowledgeUpdate) {
          // Update existing knowledge (edit mode or existing knowledge tab)
          const updatedKnowledge = {
            id: activeTab.originalId,
            title: activeTab.name.trim(),
            content: activeTab.content,
            type: "manual",
          };

          await onKnowledgeUpdate(updatedKnowledge);
          toast.success("Knowledge successfully updated");
        } else if (activeTab.isNew && onKnowledgeAdd) {
          // Create new knowledge
          const newKnowledge = {
            title: activeTab.name.trim(),
            content: activeTab.content,
            type: "manual",
          };

          await onKnowledgeAdd(newKnowledge);
          toast.success("Knowledge successfully added");
        }

        // Mark tab as saved and not dirty
        setTabs((prev) =>
          prev.map((tab) =>
            tab.id === activeTabId
              ? { ...tab, isDirty: false, isNew: false }
              : tab
          )
        );
      } catch (error) {
        toast.error("Failed to save knowledge: " + error.message);
      } finally {
        setIsSubmitting(false);
      }
    };

    // React Quill configuration (standard - no custom handlers) - EXACT SAME
    const modules = {
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link"],
        ["clean"],
      ],
    };

    const formats = [
      "header",
      "bold",
      "italic",
      "underline",
      "list",
      "bullet",
      "link",
      "image", // Add image format - SAME
    ];

    return (
      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
        {/* Tab Bar Header */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Knowledge Tabs
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              ({tabs.length})
            </span>
          </div>
          <button
            onClick={createNewTab}
            className="flex items-center space-x-1 px-2 py-1 text-sm bg-gray-600 hover:bg-gray-700 text-white rounded-md transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>New Tab</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center overflow-x-auto scrollbar-hide border-b border-gray-200 dark:border-gray-600">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className={`
              group flex items-center space-x-2 px-3 py-2 border-r border-gray-200 dark:border-gray-600 
              cursor-pointer transition-colors min-w-0 max-w-[200px]
              ${
                activeTabId === tab.id
                  ? "bg-white dark:bg-gray-800"
                  : "bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
              }
            `}
              onClick={() => handleTabClick(tab.id)}
            >
              {/* Tab Icon */}
              <Edit3 className="w-4 h-4 flex-shrink-0 text-blue-500 dark:text-blue-400" />

              {/* Tab Name - Editable */}
              {editingTabId === tab.id ? (
                <input
                  ref={editTabInputRef}
                  type="text"
                  value={editingTabName}
                  onChange={(e) => setEditingTabName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      saveTabName();
                    } else if (e.key === "Escape") {
                      cancelEditTabName();
                    }
                  }}
                  onBlur={saveTabName}
                  className="flex-1 px-1 py-0 text-sm font-medium bg-white dark:bg-gray-700 border border-blue-500 rounded text-gray-700 dark:text-gray-300 min-w-0"
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span
                  className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-300 truncate"
                  onDoubleClick={() => startEditingTabName(tab.id, tab.name)}
                >
                  {tab.name}
                </span>
              )}

              {/* Status Indicators */}
              <div className="flex items-center space-x-1 flex-shrink-0">
                {/* Dirty indicator */}
                {tab.isDirty && (
                  <div
                    className="w-2 h-2 bg-orange-500 rounded-full"
                    title="Unsaved changes"
                  />
                )}

                {/* Edit indicator */}
                {tab.isEdit && (
                  <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1 rounded">
                    edit
                  </span>
                )}

                {/* New indicator */}
                {tab.isNew && (
                  <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-1 rounded">
                    new
                  </span>
                )}

                {/* Close button */}
                <button
                  onClick={(e) => closeTab(tab.id, e)}
                  className="w-4 h-4 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                  title="Close tab"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Content Area - Unified with EXACT SAME Upload Features */}
        {activeTabId ? (
          <div
            className="p-6 bg-white dark:bg-gray-800 rounded-b-lg relative"
            data-manual-content-tab
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {/* Hidden file input - EXACT SAME */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileInputChange}
              accept="image/*"
              className="hidden"
            />

            {/* Drag overlay - EXACT SAME */}
            {isDragging && (
              <div className="absolute inset-0 bg-blue-50 border-2 border-dashed border-blue-300 flex items-center justify-center z-10 rounded-lg">
                <div className="text-center">
                  <Upload className="w-12 h-12 text-blue-500 mx-auto mb-3" />
                  <p className="text-blue-600 font-medium text-lg">
                    Drop image here
                  </p>
                  <p className="text-blue-500 text-sm">
                    Supports: JPG, PNG, GIF, WebP (Max 50MB)
                  </p>
                </div>
              </div>
            )}

            {/* Upload loading overlay - EXACT SAME */}
            {isUploading && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-20 rounded-lg">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-3"></div>
                  <p className="text-gray-600 font-medium">
                    Uploading image...
                  </p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Content Editor */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Knowledge Content *
                  </label>
                  {/* Upload button - EXACT SAME */}
                  <button
                    type="button"
                    onClick={handleUploadButtonClick}
                    disabled={isUploading}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white rounded-md transition-colors"
                  >
                    {isUploading ? (
                      <>
                        <svg
                          className="animate-spin w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Upload Image
                      </>
                    )}
                  </button>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <ReactQuill
                    key={activeTabId} // Force re-render when tab changes
                    ref={quillRef}
                    theme="snow"
                    value={getActiveTab()?.content || ""}
                    onChange={updateTabContent}
                    modules={modules}
                    formats={formats}
                    placeholder="Enter knowledge content..."
                    className="bg-white dark:bg-gray-700"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Saving...
                    </>
                  ) : getActiveTab()?.isEdit || getActiveTab()?.originalId ? (
                    "Update Knowledge"
                  ) : (
                    "Save Knowledge"
                  )}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="p-8 bg-white dark:bg-gray-800 rounded-b-lg text-center">
            <div className="text-gray-400 dark:text-gray-500 mb-2">📝</div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              No tabs open. Create a new tab to start adding knowledge.
            </p>
            <button
              onClick={createNewTab}
              className="flex items-center space-x-2 mx-auto px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Create New Tab</span>
            </button>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Knowledge"
          message={`Are you sure you want to delete "${tabToDelete?.name}"? This action cannot be undone.`}
          isLoading={false} // We can add loading state later if needed
        />
      </div>
    );
  }
);

ManualContentTab.displayName = "ManualContentTab";

export default ManualContentTab;
